declare global {
  interface Window {
    env: EnvRaw;
  }
}

export type EnvRaw = {
  HOST: string;
  NODE_ENV: string;
  PORT: string;
  REACT_APP_API_HOST: string;
  REACT_APP_FULLSTORY_ID: string;
  REACT_APP_FULLSTORY_ENABLED: string;
  REACT_APP_GOOGLE_ANALYTICS_ID: string;
  REACT_APP_DATA_DOG_ID: string;
  REACT_APP_DATA_DOG_CLIENT_TOKEN: string;
  REACT_APP_CLEARCOVER_ENV: string;
  REACT_APP_TM_ENABLED: string;
  REACT_APP_TM_ORG_ID: string;
  REACT_APP_TM_PROFILING_DOMAIN: string;
  REACT_APP_WEBSITE_HOST: string;
  REACT_APP_GOOGLE_TAG_MANAGER_ID: string;
  REACT_APP_AUTH0_DOMAIN: string;
  REACT_APP_AUTH0_CLIENT_ID: string;
  REACT_APP_AUTH0_REDIRECT_URL: string;
  REACT_APP_AUTH0_AUDIENCE: string;
  REACT_APP_PORTAL_HOST: string;
  REACT_APP_AGENT_AUTH: 'enabled' | 'disabled';
  REACT_APP_ADYEN_ENVIRONMENT: string;
  REACT_APP_ADYEN_VERSION: string;
  REACT_APP_ADYEN_CLIENT_KEY: string;
  REACT_APP_ADYEN_CARD_AVS_MODE: 'none' | 'partial' | 'full';
  REACT_APP_ADYEN_CSS_HASH: string;
  REACT_APP_ADYEN_SCRIPT_HASH: string;
  REACT_APP_RECAPTCHA_SITEKEY: string;
  REACT_APP_RECAPTCHA: 'enabled' | 'disabled';
  REACT_APP_SPUR_MONOCLE_TOKEN: string;
  REACT_APP_ALLOWED_PHONE_NUMBERS: string;
  REACT_APP_SMARTYSTREETS_ID: string;
  REACT_APP_PAYMENT_FLEXIBILITY_EVERY_TWO_WEEKS: string;
  REACT_APP_PAYMENT_FLEXIBILITY_DOWNPAYMENT_SLIDER: string;
  REACT_APP_COVERAGES_COMPONENT_BIND_API: string;
  REACT_APP_MONETIZATION_WIDGET: string;
  REACT_APP_ENHANCED_QUOTE_SUMMARY: string;
  REACT_APP_NEXTINSURE_WIDGET: string;
  REACT_APP_DISPLAY_QUOTE_MAGIC_PROGRESS_BAR?: string;
  REACT_APP_ITS_TURTKEY_TIME?: string; // seasonal turkey feature flag
  REACT_APP_TRUSTEDFORM_ENABLED: string;
  REACT_APP_SPLIT_VEHICLE_YEAR_MAKE_MODEL: string;
  REACT_APP_CONSENT_CAPTURE_ENABLED: string;
  REACT_APP_DEBUG_GTM_CONSENT_UPDATES: string;
  REACT_APP_QUOTEMAGIC_GOOGLE_TAG_MANAGER_ID?: string;
  REACT_APP_QUOTEMAGIC_GTM_AUTH?: string;
  REACT_APP_QUOTEMAGIC_GTM_PREVIEW?: string;
  REACT_APP_GATE_FULLSTORY_ON_CONSENT?: string;
  // Maintenance kill switch. Unset in normal operation; set to take over the
  // error-screen copy without a code deploy. Each overrides its field alone.
  REACT_APP_MAINTENANCE_ERROR_TITLE?: string;
  REACT_APP_MAINTENANCE_ERROR_BODY?: string;
};

type AdyenEnv = {
  adyenEnvironment: string;
  adyenVersion: string;
  adyenClientKey: string;
  adyenCssHash: string;
  adyenScriptHash: string;
  adyenCardAvsMode: 'none' | 'partial' | 'full';
};

export type Env = {
  host: string;
  nodeEnv: string;
  port: string;
  apiHost: string;
  fullstoryId: string;
  fullstoryEnabled: string;
  googleAnalyticsId: string;
  dataDogId: string;
  dataDogClientToken: string;
  clearcoverEnv: string;
  tmEnabled: string;
  tmOrgId: string;
  tmProfilingDomain: string;
  websiteHost: string;
  googleTagManagerId: string;
  auth0Domain: string;
  auth0ClientId: string;
  auth0RedirectUrl: string;
  auth0Audience: string;
  portalHost: string;
  agentAuth: 'enabled' | 'disabled';
  recaptchaSiteKey?: string;
  recaptcha: 'enabled' | 'disabled';
  spurMonocleToken: string;
  allowedPhoneNumbers?: string;
  smartystreetsId?: string;
  paymentFlexibilityEveryTwoWeeks: string;
  paymentFlexibilityDownpaymentSlider: string;
  coveragesComponentBindAPI: string;
  monetizationWidget: string;
  enhancedQuoteSummary: string;
  nextinsureWidget: string;
  displayQuoteMagicProgressBar: string;
  itsTurtkeyTime?: string; // seasonal turkey icon flag
  trustedformEnabled: string;
  splitVehicleYearMakeModel: string;
  consentCaptureEnabled: string;
  debugGtmConsentUpdates: string;
  quotemagicGoogleTagManagerId?: string;
  quotemagicGtmAuth?: string;
  quotemagicGtmPreview?: string;
  gateFullstoryOnConsent: string;
  maintenanceErrorTitle: string;
  maintenanceErrorBody: string;
} & AdyenEnv;

let defaultAdyenTestConfig: AdyenEnv = {
  adyenEnvironment: 'test',
  adyenCardAvsMode: 'none',
  adyenClientKey: '',
  adyenCssHash:
    'sha384-k6qYnmoHaucEm97eQQAs3MK5e44JQ2sksrue2tfdDOnnedKzc0VYQwxzdYWZu8Mj',
  adyenScriptHash:
    'sha384-FJWX32BY0zx3KKed9gdiWxoEAEsA3uh1ixchmdkflgtcDo+SoYg5ZD6uqvDmnafO',
  adyenVersion: '5.51.0',
};

export const defaultEnvs: Env = {
  host: 'app.localhost',
  nodeEnv: 'development',
  port: '3001',
  apiHost: 'http://api.localhost:3000',
  fullstoryId: 'JEECH',
  fullstoryEnabled: '',
  googleAnalyticsId: 'UA-98123156-2',
  dataDogId: '6d49ee3a-3417-4f69-8fc0-0e4948cc7700',
  dataDogClientToken: 'pube40929926d01844695cecd943e80ae4c',
  clearcoverEnv: 'development',
  tmEnabled: '',
  tmOrgId: 'THREATMETRIX_ORG_ID',
  tmProfilingDomain: 'THREATMETRIX',
  websiteHost: 'https://clearcoverstaging.com',
  googleTagManagerId: 'GTM-WQHRHHJ',
  auth0Domain: 'clearcover-dev.auth0.com',
  auth0ClientId: 'PvymEEzPLjAMXvEWmvQYqPIzjY52uBCn',
  auth0RedirectUrl: 'http://app.localhost:3001/agent/apply',
  auth0Audience: 'http://api.localhost:3000',
  portalHost: 'http://app.localhost:3002',
  agentAuth: 'disabled',
  ...defaultAdyenTestConfig,
  recaptchaSiteKey: '6LdDHA8pAAAAABDTdnc6lY7YISbhuDZFrcCQMgkE',
  recaptcha: 'disabled',
  spurMonocleToken: '1234',
  smartystreetsId: '',
  paymentFlexibilityEveryTwoWeeks: '0',
  paymentFlexibilityDownpaymentSlider: '0',
  coveragesComponentBindAPI: '0',
  monetizationWidget: '0',
  enhancedQuoteSummary: '0',
  nextinsureWidget: '0',
  displayQuoteMagicProgressBar: '0',
  itsTurtkeyTime: '',
  trustedformEnabled: '0',
  splitVehicleYearMakeModel: '0',
  consentCaptureEnabled: '0',
  debugGtmConsentUpdates: '1',
  gateFullstoryOnConsent: '1',
  maintenanceErrorTitle: '',
  maintenanceErrorBody: '',
};

export const defaults: { [envName: string]: Env } = {
  local: { ...defaultEnvs },
  staging: {
    ...defaultEnvs,
    apiHost: 'https://api.clearcoverstaging.com',
    clearcoverEnv: 'staging',
  },
  uat: {
    ...defaultEnvs,
    apiHost: 'https://api.clearcoveruat.com',
    clearcoverEnv: 'uat',
  },
  partner: {
    ...defaultEnvs,
    apiHost: 'https://api.clearcoverpartner.com',
    clearcoverEnv: 'partner',
  },
};

const ephemeralBaseEnvironment = process.env.REACT_APP_EPHEMERAL_BASE_ENV
  ? defaults[process.env.REACT_APP_EPHEMERAL_BASE_ENV]
  : defaults.staging;

defaults.ephemeral = {
  ...ephemeralBaseEnvironment,
  apiHost: `${process.env.REACT_APP_REZNOR_URL}`,
  clearcoverEnv: 'ephemeral',
};

// Allows npm commands to determine which backend to use
// npm start -> runs against local backend
// npm run start:staging -> runs against staging backend
// npm run start:uat -> runs against uat backend
// npm run start:partner -> runs against partner backend
// npm run start:ephemeral -> runs against ephemeral backend
const activeEnvironment =
  process.env.REACT_APP_BACKEND &&
  defaults.hasOwnProperty(process.env.REACT_APP_BACKEND)
    ? defaults[process.env.REACT_APP_BACKEND]
    : defaults.local;

// In production, server.js injects `<script>env={...}</script>` with real
// values before serving index.html. In local dev, the Vite plugin inlines
// `env={}` so the script tag is valid JS — but `{}` is truthy, so we must
// check whether it actually contains runtime vars. Without this guard,
// every field below resolves to `undefined` and the `activeEnvironment`
// defaults (selected by REACT_APP_BACKEND) never apply.
const hasRuntimeEnv = !!window.env && Object.keys(window.env).length > 0;

export const env: Env = hasRuntimeEnv
  ? {
      port: window.env.PORT,
      host: window.env.HOST,
      nodeEnv: window.env.NODE_ENV,
      apiHost: window.env.REACT_APP_API_HOST,
      fullstoryId: window.env.REACT_APP_FULLSTORY_ID,
      fullstoryEnabled: window.env.REACT_APP_FULLSTORY_ENABLED,
      googleAnalyticsId: window.env.REACT_APP_GOOGLE_ANALYTICS_ID,
      dataDogId: window.env.REACT_APP_DATA_DOG_ID,
      dataDogClientToken: window.env.REACT_APP_DATA_DOG_CLIENT_TOKEN,
      clearcoverEnv: window.env.REACT_APP_CLEARCOVER_ENV,
      tmEnabled: window.env.REACT_APP_TM_ENABLED,
      tmOrgId: window.env.REACT_APP_TM_ORG_ID,
      tmProfilingDomain: window.env.REACT_APP_TM_PROFILING_DOMAIN,
      websiteHost: window.env.REACT_APP_WEBSITE_HOST,
      googleTagManagerId: window.env.REACT_APP_GOOGLE_TAG_MANAGER_ID,
      auth0Domain: window.env.REACT_APP_AUTH0_DOMAIN,
      auth0ClientId: window.env.REACT_APP_AUTH0_CLIENT_ID,
      auth0RedirectUrl: window.env.REACT_APP_AUTH0_REDIRECT_URL,
      auth0Audience: window.env.REACT_APP_AUTH0_AUDIENCE,
      portalHost: window.env.REACT_APP_PORTAL_HOST,
      agentAuth: window.env.REACT_APP_AGENT_AUTH,
      adyenEnvironment: window.env.REACT_APP_ADYEN_ENVIRONMENT,
      adyenVersion: window.env.REACT_APP_ADYEN_VERSION,
      adyenClientKey: window.env.REACT_APP_ADYEN_CLIENT_KEY ?? '',
      adyenCardAvsMode: window.env.REACT_APP_ADYEN_CARD_AVS_MODE || 'none',
      adyenCssHash: window.env.REACT_APP_ADYEN_CSS_HASH,
      adyenScriptHash: window.env.REACT_APP_ADYEN_SCRIPT_HASH,
      recaptchaSiteKey: window.env.REACT_APP_RECAPTCHA_SITEKEY,
      recaptcha: window.env.REACT_APP_RECAPTCHA,
      spurMonocleToken: window.env.REACT_APP_SPUR_MONOCLE_TOKEN,
      allowedPhoneNumbers: window.env.REACT_APP_ALLOWED_PHONE_NUMBERS,
      smartystreetsId: window.env.REACT_APP_SMARTYSTREETS_ID,
      paymentFlexibilityEveryTwoWeeks:
        window.env.REACT_APP_PAYMENT_FLEXIBILITY_EVERY_TWO_WEEKS,
      paymentFlexibilityDownpaymentSlider:
        window.env.REACT_APP_PAYMENT_FLEXIBILITY_DOWNPAYMENT_SLIDER,
      coveragesComponentBindAPI:
        window.env.REACT_APP_COVERAGES_COMPONENT_BIND_API,
      monetizationWidget: window.env.REACT_APP_MONETIZATION_WIDGET,
      enhancedQuoteSummary: window.env.REACT_APP_ENHANCED_QUOTE_SUMMARY,
      nextinsureWidget: window.env.REACT_APP_NEXTINSURE_WIDGET,
      displayQuoteMagicProgressBar:
        window.env.REACT_APP_DISPLAY_QUOTE_MAGIC_PROGRESS_BAR ?? '0',
      itsTurtkeyTime: window.env.REACT_APP_ITS_TURTKEY_TIME,
      trustedformEnabled: window.env.REACT_APP_TRUSTEDFORM_ENABLED,
      splitVehicleYearMakeModel:
        window.env.REACT_APP_SPLIT_VEHICLE_YEAR_MAKE_MODEL,
      consentCaptureEnabled: window.env.REACT_APP_CONSENT_CAPTURE_ENABLED,
      debugGtmConsentUpdates: window.env.REACT_APP_DEBUG_GTM_CONSENT_UPDATES,
      quotemagicGoogleTagManagerId:
        window.env.REACT_APP_QUOTEMAGIC_GOOGLE_TAG_MANAGER_ID,
      quotemagicGtmAuth: window.env.REACT_APP_QUOTEMAGIC_GTM_AUTH,
      quotemagicGtmPreview: window.env.REACT_APP_QUOTEMAGIC_GTM_PREVIEW,
      gateFullstoryOnConsent:
        window.env.REACT_APP_GATE_FULLSTORY_ON_CONSENT ?? '1',
      maintenanceErrorTitle: window.env.REACT_APP_MAINTENANCE_ERROR_TITLE ?? '',
      maintenanceErrorBody: window.env.REACT_APP_MAINTENANCE_ERROR_BODY ?? '',
    }
  : activeEnvironment;
